import React from 'react';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import {
  type AddressType,
  BodyText,
  Button,
  capitalizeFirstLetter,
  Card,
  theme,
  useResponsive,
  useToggleState,
} from '@sg/garnish';

import { ignoreTimezone } from '@order/utils';

import { OrderCancellationModal, useOrderCancellationModal } from '../OrderCancellationModal';
import { OrderCardImage } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderCard = (props: OrderCardProps) => {
  const { orderId, restaurantId, isCanceled, isCancellable, onPress, onOrderCancellationSuccess } =
    props;

  const { formatMessage } = useIntl();

  const shouldRenderCanceledLabel = isCanceled && !isCancellable;

  return (
    <Card hasHoverState>
      <Pressable
        accessibilityRole="button"
        aria-label={formatMessage(messages.orderCardPressableA11yLabel, {
          order_id: orderId,
        })}
        onPress={onPress}
        style={styles.cardPressable}
      />

      <View style={styles.content} pointerEvents="box-none">
        <View pointerEvents="none">
          <OrderCardImage {...props} />
        </View>

        <View style={styles.info} pointerEvents="box-none">
          <View pointerEvents="none">
            <OrderCardTitle {...props} />
            <OrderCardLocation {...props} />
            <OrderCardDescription {...props} />

            <View style={styles.priceAndStatusContainer}>
              {shouldRenderCanceledLabel ? <OrderCardCanceledLabel /> : null}
              <OrderCardTotal {...props} />
            </View>
          </View>

          {isCancellable ? (
            <OrderCardQuickCancel
              orderId={orderId}
              restaurantId={restaurantId}
              onOrderCancellationSuccess={onOrderCancellationSuccess}
            />
          ) : null}
        </View>
      </View>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTitle = (props: Pick<OrderCardProps, 'wantedTime' | 'orderType'>) => {
  const { wantedTime, orderType } = props;

  const { formatMessage } = useIntl();

  const normalizedWantedTime = ignoreTimezone(wantedTime);
  const capitalizedOrderType = capitalizeFirstLetter(orderType);
  const title = normalizedWantedTime
    ? formatMessage(messages.orderCardTitle, {
        wanted_time: normalizedWantedTime,
        order_type: capitalizedOrderType,
      })
    : capitalizedOrderType;

  return <BodyText bold>{title}</BodyText>;
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardLocation = (
  props: Pick<OrderCardProps, 'orderType' | 'restaurantName' | 'addressName'>,
) => {
  const { orderType, restaurantName, addressName } = props;

  return (
    <BodyText sizeMatch={['14', '16']}>
      {orderType === 'delivery' && addressName
        ? capitalizeFirstLetter(addressName)
        : restaurantName}
    </BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardDescription = (props: Pick<OrderCardProps, 'description'>) => (
  <View style={styles.description}>
    <BodyText numberOfLines={2} ellipsizeMode="tail" sizeMatch={['14', '16']}>
      {props.description}
    </BodyText>
  </View>
);

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardTotal = (props: Pick<OrderCardProps, 'total'>) => {
  return (
    <BodyText sizeMatch={['14', '16']}>
      <FormattedNumber
        value={props.total / 100}
        // eslint-disable-next-line react/style-prop-object
        style="currency"
        currency="USD"
      />
    </BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardCanceledLabel = () => {
  const { match } = useResponsive();

  return (
    <BodyText
      bold
      style={match([styles.isCanceledLabelXS, styles.isCanceledLabelSM])}
      sizeMatch={['14', '16']}
    >
      <FormattedMessage {...messages.orderCanceled} />
    </BodyText>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const OrderCardQuickCancel = (props: OrderCardQuickCancelProps) => {
  const { orderId, restaurantId, onOrderCancellationSuccess } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const {
    isOrderCancellationModalVisible,
    showOrderCancellationModal,
    hideOrderCancellationModal,
  } = useOrderCancellationModal();

  const {
    value: shouldHideCancelDuringAsyncCancelation,
    toggleOn: hideCancelDuringAsyncCancelation,
  } = useToggleState();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {shouldHideCancelDuringAsyncCancelation ? null : (
        <View style={styles.cancelButtonContainer} pointerEvents="auto">
          <Button palette="secondary" onPress={showOrderCancellationModal}>
            <FormattedMessage {...messages.cancelOrder} />
          </Button>
        </View>
      )}

      <OrderCancellationModal
        orderId={orderId}
        restaurantId={restaurantId}
        isVisible={isOrderCancellationModalVisible}
        onSuccess={onOrderCancellationSuccess}
        onAsyncCancelation={hideCancelDuringAsyncCancelation}
        onRequestClose={hideOrderCancellationModal}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderCardTitle: {
    defaultMessage: '{wanted_time, date, short} · {order_type}',
    description: 'Order Card | Title format',
  },
  orderCardPressableA11yLabel: {
    defaultMessage: 'Open the {order_id} order status screen.',
    description: 'Order Card | Accessibility label',
  },
  cancelOrder: {
    defaultMessage: 'Cancel order',
    description: 'Order Card | Cancel button text',
  },
  orderCanceled: {
    defaultMessage: 'Canceled ·',
    description: 'Order Card | Canceled label text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing['6'],
    zIndex: 1,
  },
  cardPressable: {
    ...StyleSheet.absoluteFill,
    width: '100%',
  },
  info: {
    flex: 1,
    marginLeft: theme.spacing['4'],
  },
  description: {
    marginVertical: theme.spacing['2'],
  },
  cancelButtonContainer: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing['2'],
  },
  priceAndStatusContainer: {
    flexDirection: 'row',
    gap: theme.spacing['1'],
  },
  isCanceledLabelXS: {
    lineHeight: 20, // to match other text elements baseline
  },
  isCanceledLabelSM: {
    lineHeight: 24, // to match other text elements baseline
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderCardProps = Readonly<{
  orderId: string;
  orderType: AddressType;
  wantedTime: string;
  restaurantId: string;
  restaurantName?: string;
  addressName?: string;
  description: string;
  total: number;
  assetUrl: string;
  isCanceled: boolean;
  isCancellable?: boolean;
  onPress: () => void;
  onOrderCancellationSuccess: (orderId: string) => void;
}>;

type OrderCardQuickCancelProps = Readonly<{
  orderId: string;
  restaurantId: string;
  onOrderCancellationSuccess: (orderId: string) => void;
}>;
